import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { toPng } from "html-to-image";
import ScreenshotLangPicker from "../../components/ScreenshotLangPicker";
import { appendScreenshotLang, useScreenshotTranslation } from "../../lib/screenshotI18n";
import { SLIDING_PUZZLE_QUEST_TITLE, screenshotPath } from "../../prototype/mobile-sliding/routes";
import {
    isScreenshotSceneConfigured,
    SLIDING_SCREENSHOT_SCENES,
} from "../../prototype/mobile-sliding/screenshotScenes";
import { slidingShellBgForScene } from "../../prototype/mobile-sliding/styles/screenshotStyles";
import { downloadPngDataUrl } from "./downloadPngDataUrl";
import {
    persistGoogleAdFormatTab,
    readGoogleAdFormatTab,
} from "./googleAdFormatTabStorage";

/** Google Ads image sizes. */
const AD_FORMATS = [
    { id: "landscape", label: "Landscape", width: 1200, height: 628 },
    { id: "square", label: "Square", width: 1200, height: 1200 },
    { id: "portrait", label: "Portrait", width: 1080, height: 1920 },
];

const FORMAT_TAB_STORAGE_KEY = "slidingGoogleAd.formatTab";

/** Match typical phone capture window for Sliding Puzzle Quest screenshots. */
const PHONE_W = 430;
const PHONE_H = 940;
const PREVIEW_MAX_W = 180;
const PREVIEW_MAX_W_SHOWCASE = Math.round((PREVIEW_MAX_W * 2 * 1.5) / 2);
/** Home, Giant, Gallery — stretch to fill the portrait ad frame. */
const STRETCH_FILL_SLUGS = new Set(["01", "04", "06"]);

const TO_PNG_OPTS = {
    cacheBust: true,
    pixelRatio: 1,
    skipFonts: true,
};

function usesStretchFill(scene) {
    return STRETCH_FILL_SLUGS.has(scene?.slug);
}

function screenshotPageHref(path, lang, format) {
    if (typeof window === "undefined") return path;
    const withLang = appendScreenshotLang(path, lang);
    const url = new URL(withLang, window.location.origin);
    if (format.id === "square") {
        url.searchParams.set("googleAdSquare", "1");
    }
    if (format.id === "landscape") {
        url.searchParams.set("googleAdLandscape", "1");
    }
    return url.toString();
}

async function loadImage(src) {
    const img = new Image();
    await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error("Failed to decode capture"));
        img.src = src;
    });
    return img;
}

async function capturePhonePng(iframe) {
    const doc = iframe?.contentDocument;
    if (!doc?.documentElement) {
        throw new Error("Preview still loading — try again in a moment");
    }
    return toPng(doc.documentElement, {
        ...TO_PNG_OPTS,
        width: PHONE_W,
        height: PHONE_H,
        backgroundColor: null,
    });
}

async function fillWithCssBackground(ctx, width, height, background) {
    const mount = document.createElement("div");
    mount.setAttribute("aria-hidden", "true");
    mount.style.cssText = [
        "position:fixed",
        "left:-10000px",
        "top:0",
        `width:${width}px`,
        `height:${height}px`,
        `background:${background}`,
        "pointer-events:none",
    ].join(";");
    document.body.appendChild(mount);
    try {
        const url = await toPng(mount, {
            ...TO_PNG_OPTS,
            width,
            height,
        });
        const img = await loadImage(url);
        ctx.drawImage(img, 0, 0, width, height);
    } finally {
        document.body.removeChild(mount);
    }
}

async function buildPortraitAdPngDataUrl(iframe, width, height, shellBg, stretchFill = false) {
    const srcDataUrl = await capturePhonePng(iframe);
    const img = await loadImage(srcDataUrl);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas unavailable");

    await fillWithCssBackground(ctx, width, height, shellBg);

    if (stretchFill) {
        ctx.drawImage(img, 0, 0, width, height);
    } else {
        const scale = Math.min(width / img.width, height / img.height);
        const drawW = img.width * scale;
        const drawH = img.height * scale;
        ctx.drawImage(img, (width - drawW) / 2, (height - drawH) / 2, drawW, drawH);
    }
    return canvas.toDataURL("image/png");
}

async function buildShowcaseAdPngDataUrl(iframe, width, height) {
    const doc = iframe?.contentDocument;
    if (!doc?.documentElement) {
        throw new Error("Preview still loading — try again in a moment");
    }
    const frame =
        doc.querySelector('[data-google-ad-square="1"]') ||
        doc.querySelector('[data-google-ad-landscape="1"]') ||
        doc.documentElement;
    const swaps = [];
    const shotFrames = [...doc.querySelectorAll("iframe[data-portrait-shot]")];
    try {
        for (const shotFrame of shotFrames) {
            const shotDoc = shotFrame.contentDocument?.documentElement;
            if (!shotDoc) continue;
            const url = await toPng(shotDoc, {
                ...TO_PNG_OPTS,
                width: PHONE_W,
                height: PHONE_H,
                backgroundColor: "#f2f2f7",
            });
            const img = doc.createElement("img");
            img.alt = "";
            img.src = url;
            const style = shotFrame.ownerDocument.defaultView.getComputedStyle(shotFrame);
            img.style.position = "absolute";
            img.style.left = style.left;
            img.style.top = style.top;
            img.style.width = style.width;
            img.style.height = style.height;
            img.style.transform = style.transform;
            img.style.transformOrigin = style.transformOrigin;
            img.style.objectFit = "cover";
            img.style.objectPosition = "center top";
            img.style.border = "0";
            shotFrame.parentElement?.insertBefore(img, shotFrame);
            shotFrame.style.visibility = "hidden";
            swaps.push({ shotFrame, img });
            await loadImage(url);
        }
        return await toPng(frame, {
            ...TO_PNG_OPTS,
            width,
            height,
        });
    } finally {
        swaps.forEach(({ shotFrame, img }) => {
            img.remove();
            shotFrame.style.visibility = "";
        });
    }
}

function AdScreenshotTile({ format, scene, lang }) {
    const iframeRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [iframeReady, setIframeReady] = useState(false);

    const isShowcase = format.id === "square" || format.id === "landscape";
    const href = screenshotPageHref(screenshotPath(scene.slug), lang, format);
    const shellBg = slidingShellBgForScene(scene);
    const stretchFill = !isShowcase && usesStretchFill(scene);
    const previewMaxW = isShowcase ? PREVIEW_MAX_W_SHOWCASE : PREVIEW_MAX_W;
    const previewScale = Math.min(1, previewMaxW / format.width);
    const previewW = Math.round(format.width * previewScale);
    const previewH = Math.round(format.height * previewScale);
    const fitScaleX = stretchFill
        ? format.width / PHONE_W
        : Math.min(format.width / PHONE_W, format.height / PHONE_H);
    const fitScaleY = stretchFill ? format.height / PHONE_H : fitScaleX;

    useEffect(() => {
        setIframeReady(false);
    }, [href, format.id]);

    const handleDownload = useCallback(async () => {
        if (saving) return;
        setSaving(true);
        setSaveError(null);
        try {
            const dataUrl = isShowcase
                ? await buildShowcaseAdPngDataUrl(
                      iframeRef.current,
                      format.width,
                      format.height,
                  )
                : await buildPortraitAdPngDataUrl(
                      iframeRef.current,
                      format.width,
                      format.height,
                      shellBg,
                      stretchFill,
                  );
            await downloadPngDataUrl(
                dataUrl,
                `sliding-puzzle-quest-google-ad-${scene.slug}-${format.id}-${format.width}x${format.height}.png`,
            );
        } catch (e) {
            setSaveError(e?.message || "Failed to download PNG");
        } finally {
            setSaving(false);
        }
    }, [format, scene.slug, saving, shellBg, stretchFill, isShowcase]);

    return (
        <Box
            sx={{
                flex: `0 0 ${previewW}px`,
                width: previewW,
                mb: 1.5,
            }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25, fontSize: 12 }}>
                Screenshot {scene.slug}
            </Typography>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    display: "block",
                    mb: 0.75,
                    minHeight: 28,
                    fontSize: 11,
                    lineHeight: 1.25,
                }}
            >
                {scene.label}
            </Typography>
            <Box
                sx={{
                    width: previewW,
                    height: previewH,
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    boxShadow: 1,
                    background: isShowcase ? "#0f172a" : shellBg,
                }}
            >
                <Box
                    sx={{
                        width: format.width,
                        height: format.height,
                        transform: `scale(${previewScale})`,
                        transformOrigin: "top left",
                        overflow: "hidden",
                        position: "relative",
                        background: isShowcase ? "#0f172a" : shellBg,
                    }}
                >
                    <Box
                        component="iframe"
                        ref={iframeRef}
                        src={href}
                        title={`Screenshot ${scene.slug} · ${format.label}`}
                        loading="lazy"
                        onLoad={() => {
                            // Phone shots render after the portrait screen is captured.
                            window.setTimeout(
                                () => setIframeReady(true),
                                isShowcase ? 2800 : 900,
                            );
                        }}
                        sx={
                            isShowcase
                                ? {
                                      position: "absolute",
                                      left: 0,
                                      top: 0,
                                      width: format.width,
                                      height: format.height,
                                      border: 0,
                                      pointerEvents: "none",
                                      display: "block",
                                      background: "#0f172a",
                                  }
                                : {
                                      position: "absolute",
                                      left: stretchFill ? 0 : "50%",
                                      top: stretchFill ? 0 : "50%",
                                      width: PHONE_W,
                                      height: PHONE_H,
                                      border: 0,
                                      transform: stretchFill
                                          ? `scale(${fitScaleX}, ${fitScaleY})`
                                          : `translate(-50%, -50%) scale(${fitScaleX})`,
                                      transformOrigin: stretchFill
                                          ? "top left"
                                          : "center center",
                                      pointerEvents: "none",
                                      display: "block",
                                      background: "transparent",
                                  }
                        }
                    />
                </Box>
            </Box>
            <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={
                        saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon />
                    }
                    onClick={handleDownload}
                    disabled={saving || !iframeReady}
                    sx={{ minHeight: 36, textTransform: "none", fontWeight: 700 }}
                >
                    {saving ? "Saving…" : "Download"}
                </Button>
                {saveError ? (
                    <Typography variant="caption" color="error">
                        {saveError}
                    </Typography>
                ) : null}
            </Stack>
        </Box>
    );
}

/**
 * Admin page — Google Ads creatives from all Sliding Puzzle Quest screenshots
 * in Landscape / Square / Portrait, with per-shot download.
 */
export default function SlidingGoogleAdPage() {
    const { lang } = useScreenshotTranslation();
    const [formatTab, setFormatTab] = useState(() =>
        readGoogleAdFormatTab(AD_FORMATS, FORMAT_TAB_STORAGE_KEY),
    );
    const scenes = useMemo(
        () => SLIDING_SCREENSHOT_SCENES.filter(isScreenshotSceneConfigured),
        [],
    );
    const activeFormat = AD_FORMATS[formatTab] ?? AD_FORMATS[0];

    useEffect(() => {
        persistGoogleAdFormatTab(AD_FORMATS, FORMAT_TAB_STORAGE_KEY, formatTab);
    }, [formatTab]);

    return (
        <Box sx={{ p: 2, pb: 6, maxWidth: 1400, mx: "auto" }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {SLIDING_PUZZLE_QUEST_TITLE} — Google Ads
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                All screenshots (01–{String(scenes.length).padStart(2, "0")}) · three Google Ads
                sizes · download PNG under each preview
            </Typography>
            <ScreenshotLangPicker sx={{ mb: 2 }} />

            <Tabs
                value={formatTab}
                onChange={(_, next) => setFormatTab(next)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                    mb: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                    minHeight: 48,
                }}
            >
                {AD_FORMATS.map((format, index) => (
                    <Tab
                        key={format.id}
                        label={`${format.label} · ${format.width}×${format.height}`}
                        id={`sliding-google-ad-tab-${index}`}
                        aria-controls={`sliding-google-ad-tabpanel-${index}`}
                        sx={{ minHeight: 48, textTransform: "none", fontWeight: 600 }}
                    />
                ))}
            </Tabs>

            <Box
                role="tabpanel"
                id={`sliding-google-ad-tabpanel-${formatTab}`}
                aria-labelledby={`sliding-google-ad-tab-${formatTab}`}
            >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {activeFormat.id === "square" || activeFormat.id === "landscape"
                        ? `Dedicated ${activeFormat.label.toLowerCase()} creatives (${activeFormat.width}×${activeFormat.height}) · phones show the portrait shots`
                        : `Shell background fills ${activeFormat.label.toLowerCase()} (${activeFormat.width}×${activeFormat.height}) · portrait UI kept in ratio`}
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.5,
                        alignItems: "flex-start",
                    }}
                >
                    {scenes.map((scene) => (
                        <AdScreenshotTile
                            key={`${activeFormat.id}-${scene.slug}`}
                            format={activeFormat}
                            scene={scene}
                            lang={lang}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
